import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Link} from '@github-ui/react-core/link'
import {isUIService} from '@github-ui/runtime-environment'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {getPrimaryKeybinding} from '@github-ui/ui-commands'
import {CommandPaletteIcon, SearchIcon} from '@primer/octicons-react'
import {ButtonGroup, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {lazy, startTransition, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'

import {NavButtonItem, NavLinkItem} from '../../primitives/NavItemLink'
import {shouldHideTopNav, useTopNavContext} from '../../primitives/TopNav'
import {useGlobalNavAnalytics} from '../../utils/Analytics'
import styles from './Search.module.css'

const QuickSearch = lazy(async () => {
  const module = await import('@github-ui/quick-search')
  return {default: module.QuickSearch}
})

async function preloadQuickSearch() {
  const module = await import('@github-ui/quick-search')
  module.preloadQuickSearch()
}

export type SearchProps = {
  show?: boolean
  showCommandPalette?: boolean
  isSearchPage?: boolean
  isJumpToSearch?: boolean
  searchContext?: Record<string, string>
}

export function SearchPlaceholder({isSearchPage, isLegacySearch}: {isSearchPage?: boolean; isLegacySearch?: boolean}) {
  const urlParams = new URLSearchParams(ssrSafeLocation.search)
  const query = urlParams.get('q')?.trim()

  if (query && isSearchPage) {
    return (
      <span className={clsx(styles.placeholder, styles.text, styles.value, isLegacySearch && styles.searchText)}>
        {query}
      </span>
    )
  }

  return (
    <span className={clsx(styles.placeholder, styles.text, styles.value)}>
      Type <kbd className={styles.kbd}>{getPrimaryKeybinding('global-navigation:search')}</kbd> to search
    </span>
  )
}

export function Search({show, isJumpToSearch, showCommandPalette, isSearchPage, searchContext}: SearchProps) {
  const {sendItemClick} = useGlobalNavAnalytics('QUICK_SEARCH')
  const {searchOpen, setSearchOpen, setTopNavHidden, topNavHidden, loadCopilotChat} = useTopNavContext()
  const searchKeybinding = getPrimaryKeybinding('global-navigation:search') || '/'
  const quickSearchAriaLabel = `Open quick search dialog, type ${searchKeybinding} to search`
  const showNewQuickSearch = isUIService
    ? isFeatureEnabled('new_quick_search_ui_service')
    : isFeatureEnabled('new_quick_search_dotcom')

  const [searchTextBlocks, setSearchTextBlocks] = useState<SearchTextBlock[]>([])
  const [searchLoaded, setSearchLoaded] = useState(false)
  const [isQuickSearchOpen, setIsQuickSearchOpen] = useState(false)
  const scope = searchContext?.scope || ''

  // Kick off the module import (and its data pre-warm) immediately on mount so
  // users who open via keyboard shortcut get instant results, not just hover users.
  useEffect(() => {
    if (showNewQuickSearch) void preloadQuickSearch()
  }, [showNewQuickSearch])

  const handleQuickSearchIntent = useCallback(() => {
    loadCopilotChat()
    void preloadQuickSearch()
  }, [loadCopilotChat])

  const openQuickSearch = useCallback(() => {
    void preloadQuickSearch()
    startTransition(() => {
      setIsQuickSearchOpen(true)
    })
  }, [])

  const closeQuickSearch = useCallback(() => {
    startTransition(() => {
      setIsQuickSearchOpen(false)
    })
  }, [])

  const handleQuickSearchSubmit = useCallback(
    (_query: string) => {
      sendItemClick('SEARCH_SUBMISSION')
    },
    [sendItemClick],
  )

  const handleQuickSearchSuggestionSelect = useCallback(
    (type: 'jump-to' | 'search' | 'autocomplete') => {
      sendItemClick('SUGGESTION_SELECTION', type)
    },
    [sendItemClick],
  )

  const getHref = () => {
    if (isUIService) {
      return scope ? {href: `/search?${new URLSearchParams({q: scope}).toString()}`} : {href: '/search'}
    } else {
      return {}
    }
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(ssrSafeLocation.search)

    const hideTopNav =
      shouldHideTopNav(urlParams, isSearchPage ?? false, isJumpToSearch ?? false) && !searchTextBlocks.length

    if (topNavHidden !== hideTopNav) {
      setTopNavHidden(hideTopNav)
    }
  }, [setTopNavHidden, topNavHidden, isSearchPage, isJumpToSearch, searchTextBlocks])

  if (!show) {
    return null
  }

  if (isJumpToSearch && !showNewQuickSearch) {
    return <JumpToSearch showCommandPalette={showCommandPalette} isSearchPage={isSearchPage} />
  }
  return (
    <>
      <ButtonGroup
        className={clsx(styles.searchButtonGroup, isSearchPage && styles.searchPage, searchOpen && styles.hideButtons)}
      >
        {showNewQuickSearch ? (
          <NavButtonItem
            className={styles.searchButton}
            onClick={openQuickSearch}
            onFocus={handleQuickSearchIntent}
            onMouseEnter={handleQuickSearchIntent}
            icon={SearchIcon}
            analyticsKey="SEARCH"
            aria-label={quickSearchAriaLabel}
            aria-haspopup="dialog"
            aria-expanded={isQuickSearchOpen}
            linkText={
              <span className={clsx(styles.placeholder, styles.text, styles.value)}>
                Type <kbd className={styles.kbd}>{searchKeybinding}</kbd> to search
              </span>
            }
          />
        ) : (
          <NavLinkItem
            className={styles.searchButton}
            aria-busy={!searchLoaded}
            onClick={() => {
              if (!isUIService && searchLoaded) {
                setSearchOpen(true)
              }
            }}
            onFocus={loadCopilotChat}
            onMouseEnter={loadCopilotChat}
            icon={SearchIcon}
            {...getHref()}
            analyticsKey="SEARCH"
            aria-label="Search or jump to…"
            linkText={
              searchTextBlocks && searchTextBlocks.length > 0 ? (
                searchTextBlocks.map((block, index) => {
                  return (
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    <span key={`value-${index}`} className={clsx(styles.text, styles[block.type])}>
                      {block.value}
                    </span>
                  )
                })
              ) : (
                <SearchPlaceholder isSearchPage={isSearchPage} />
              )
            }
          />
        )}
        {showCommandPalette && !showNewQuickSearch && (
          <NavButtonItem
            className="js-activate-command-palette"
            icon={CommandPaletteIcon}
            aria-label="Command palette"
            analyticsKey="COMMAND_PALETTE"
          />
        )}
      </ButtonGroup>
      {showNewQuickSearch ? (
        <NavButtonItem
          icon={SearchIcon}
          aria-label={quickSearchAriaLabel}
          commandId="global-navigation:search"
          onClick={openQuickSearch}
          onFocus={handleQuickSearchIntent}
          onMouseEnter={handleQuickSearchIntent}
          className={styles.smallSearchButton}
          analyticsKey="SEARCH"
        />
      ) : (
        <NavLinkItem
          icon={SearchIcon}
          aria-label="Search or jump to…"
          commandId="global-navigation:search"
          {...getHref()}
          onClick={() => {
            if (isUIService) return
            if (searchLoaded) {
              setSearchOpen(true)
            }
          }}
          onFocus={loadCopilotChat}
          onMouseEnter={loadCopilotChat}
          className={styles.smallSearchButton}
          analyticsKey="SEARCH"
        />
      )}
      <BlackbirdSearch
        open={searchOpen}
        onLoad={() => setSearchLoaded(true)}
        onClose={closed => {
          setSearchOpen(!closed)
        }}
        onSearchBlocksChange={setSearchTextBlocks}
      />
      {showNewQuickSearch && isQuickSearchOpen && (
        <Suspense fallback={null}>
          <QuickSearch
            isOpen={isQuickSearchOpen}
            onClose={closeQuickSearch}
            onSearchSubmit={handleQuickSearchSubmit}
            onSuggestionSelect={handleQuickSearchSuggestionSelect}
            searchContext={searchContext}
          />
        </Suspense>
      )}
    </>
  )
}

function JumpToSearch({showCommandPalette, isSearchPage}: SearchProps) {
  // Mirrors the same condition that causes SearchPlaceholder to render
  // `.searchText` for legacy search, so we can set a class directly on the
  // ButtonGroup instead of relying on `:has(.searchText)`.
  const hasSearchText = Boolean(isSearchPage && new URLSearchParams(ssrSafeLocation.search).get('q')?.trim())
  return (
    <span className="AppHeader-search">
      <ButtonGroup className={clsx(styles.placeholderButtonGroup, hasSearchText && styles.hasSearchText)}>
        <NavLinkItem
          className={clsx(styles.searchButton, styles.placeholderButton)}
          icon={SearchIcon}
          href="/search"
          analyticsKey="SEARCH"
          aria-label="Search or jump to…"
          linkText={<SearchPlaceholder isSearchPage={isSearchPage} isLegacySearch />}
        />
        {showCommandPalette && (
          <NavButtonItem
            className="js-activate-command-palette"
            icon={CommandPaletteIcon}
            aria-label="Command palette"
            analyticsKey="COMMAND_PALETTE"
          />
        )}
      </ButtonGroup>
      <IconButton
        as={Link}
        icon={SearchIcon}
        to="/search"
        aria-label="Search or jump to…"
        className={clsx(styles.mobileSearch, styles.mobilePlaceholderButton)}
      />
    </span>
  )
}

function BlackbirdSearch(props: {
  open?: boolean
  onClose?: (closed: boolean) => void
  onSearchBlocksChange?: (blocks: SearchTextBlock[]) => void
  onLoad?: () => void
}) {
  const {onLoad, onClose, open, onSearchBlocksChange} = props
  const searchInputWrapperRef = useRef<HTMLDivElement | null>(null)

  const [loaded, setLoaded] = useState(false)
  const [hasChildDialogOpen, setHasChildDialogOpen] = useState(false)

  useLayoutEffect(() => {
    // Wait until the DOM is attached to mark as loaded
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoaded(true)
    onLoad?.()
  }, [onLoad])

  const onInitialize = useCallback(() => {
    const searchInput = document.querySelector('qbsearch-input')
    if (!searchInput || !isSearchInputElement(searchInput)) {
      return
    }

    const clonedSearchInput = searchInput.cloneNode(true)
    searchInputWrapperRef.current?.appendChild(clonedSearchInput)

    const suggestionsTokenInput = document.querySelector('.js-data-jump-to-suggestions-path-csrf')
    if (suggestionsTokenInput) {
      const clonedSuggestionsTokenInput = suggestionsTokenInput.cloneNode(true)
      searchInputWrapperRef.current?.appendChild(clonedSuggestionsTokenInput)
    }
  }, [])

  useSearchElement(searchInputWrapperRef, {
    loaded,
    onClose,
    open,
    onSearchBlocksChange,
    onInitialize,
    onChildDialogChange: setHasChildDialogOpen,
  })

  // Visible when search is open OR when a child dialog (like feedback) is open
  const isVisible = open || hasChildDialogOpen

  return (
    <div
      className={clsx({
        'd-flex width-full': open,
        'd-none': !isVisible,
        // When child dialog is open but search is closed, use absolute positioning
        // to keep child dialogs visible without affecting layout
        [styles.childDialogOpen]: hasChildDialogOpen && !open,
      })}
      ref={searchInputWrapperRef}
    />
  )
}

/**
 * A partial definition of the qbsearch-input web component's element interface
 */
export type SearchInputElement = Element & {
  handleExpand: () => void
  closeDialog: () => void
  syncRichButtonText: () => void
}

function isSearchInputElement(element: Element | null): element is SearchInputElement {
  return element !== null && 'handleExpand' in element && 'closeDialog' in element && 'syncRichButtonText' in element
}

/**
 * A block of text in the search input, representing either a filter, constant text, entity, or value
 */
export type SearchTextBlock = {
  type: 'filter' | 'constant' | 'entity' | 'value'
  value: string
}

function useSearchElement(
  containerRef: React.RefObject<HTMLElement | null>,
  options: {
    loaded?: boolean
    open?: boolean
    onClose?: (closed: boolean) => void
    onSearchBlocksChange?: (blocks: SearchTextBlock[]) => void
    onInitialize?: () => void
    onChildDialogChange?: (hasOpenDialog: boolean) => void
  },
) {
  const searchInputRef = useRef<SearchInputElement | null>(null)
  const {onInitialize, onChildDialogChange} = options
  const container = containerRef.current

  const initializeSearch = useCallback(() => {
    if (!options.loaded || !container || searchInputRef.current) {
      return
    }

    onInitialize?.()

    // Move the search input into the center section of the navigation bar
    const searchInput = container.querySelector('qbsearch-input')
    if (!searchInput || !isSearchInputElement(searchInput)) {
      return
    }

    searchInputRef.current = searchInput

    searchInputRef.current.syncRichButtonText()
  }, [container, options.loaded, onInitialize])

  useEffect(() => {
    if (options.open) {
      initializeSearch()
      searchInputRef.current?.handleExpand()
    }
  }, [options.open, initializeSearch])

  useEffect(() => {
    const onCloseSearch = () => {
      options.onClose?.(true)
    }

    const onOpenSearch = () => {
      onChildDialogChange?.(false)
      options.onClose?.(false)
    }

    const onUpdateText = (event: Event) => {
      if (!(event instanceof CustomEvent)) return
      options.onSearchBlocksChange?.(event.detail.searchTextBlocks as SearchTextBlock[])
    }

    // Initialize search input on mount
    initializeSearch()

    // Always listen for the updateText event so we get updates to the search text when the nav is hidden on `/search`
    document.addEventListener('qbsearch-input:updateText', onUpdateText)

    const searchInput = searchInputRef.current
    if (!searchInput) {
      return () => {
        document.removeEventListener('qbsearch-input:updateText', onUpdateText)
      }
    }

    document.addEventListener('qbsearch-input:close', onCloseSearch)
    document.addEventListener('qbsearch-input:expand', onOpenSearch)

    // Use MutationObserver to detect when child dialogs open or close
    // This handles the feedback dialog which opens after the search dialog closes
    const observer = new MutationObserver(() => {
      const hasOpenDialog = Boolean(
        searchInput.querySelector('dialog[open]') || searchInput.querySelector('modal-dialog[open]'),
      )
      onChildDialogChange?.(hasOpenDialog)
    })

    observer.observe(searchInput, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['open'],
    })

    return () => {
      document.removeEventListener('qbsearch-input:close', onCloseSearch)
      document.removeEventListener('qbsearch-input:expand', onOpenSearch)
      document.removeEventListener('qbsearch-input:updateText', onUpdateText)
      observer.disconnect()
    }
  }, [initializeSearch, onChildDialogChange, options])
}
